/* ATHLO+ — Locker layout (ADDON, removable) ==================================
 *
 * WHAT THIS DOES. The Locker's markup is one long source-order stack of ~15
 * blocks. This script rearranges it, at runtime, into a dashboard: identity at
 * the top, a two-lane grid of cards, then the full-width blocks that want the
 * width, then a utility strip. Pure appendChild moves — every element id and
 * every listener already bound to it survives — so no locker JS changes.
 *
 * WHY IT IS DECLARED, NOT PACKED. The previous version distributed cards into
 * whichever column was currently SHORTER (greedy bin-packing). That balanced
 * the two column bottoms and cost the page its hierarchy: "Invite a friend"
 * could outrank "Titles & achievements", and because cards grow as the profile
 * hydrates, the order re-shuffled at 700ms, at 1600ms, and on every resize. A
 * dashboard that rearranges itself while you read it is the thing that felt
 * messy. Order is now DECLARED below and never changes. The canyon that packing
 * existed to prevent is already prevented at source: the achievements wall is
 * height-capped (`.lk-ach__grid { max-height: min(56vh,540px) }`), so no single
 * card can tower over the other lane any more.
 *
 * WHAT MOVED, AND WHY
 *   overlays first   #lk-qr / #lk-avbuild / #lk-editor now sit DIRECTLY under
 *                    the hero, because that is where the buttons that open them
 *                    are. They used to open six blocks further down.
 *   one points card  #lk-rewards-panel is folded INTO #lk-membership for signed-
 *                    in members. Its <h3> hydrates to "1,240 points · Pro",
 *                    which is verbatim what the Membership card two columns
 *                    away already says — two cards, both titled Membership.
 *                    Signed out there is no Membership card, so it stays in the
 *                    utility strip where it works as the pitch.
 *   tiers stay wide  .lk-tiers-wrap is NOT lifted into a lane. Three tier cards
 *                    in a half-width column wrap to a cramped 2+1; that was
 *                    tried on 2026-07-17 and deliberately reverted. It keeps its
 *                    full width and its own disclosure, and now defaults to
 *                    CLOSED for members (their tier is already in the card
 *                    above) and OPEN for guests (for whom it is the pitch).
 *
 * COLLAPSIBLE, AND IT ARRIVES TIDY. Four blocks shipped with a +/- disclosure
 * (preview, onboard, tiers, saved) and four equally long ones had none. Every
 * card now carries the same chevron, persisted per card in localStorage, plus
 * one Collapse-all in the hero action row for the whole page in a click.
 *
 * The part that matters is what is OPEN when you arrive. Eight expanded blocks
 * is the clutter, so the first-visit state is chosen — see FOLD_DEFAULT and
 * LEGACY below — on one rule: open if it is something to act on or something
 * that changed; closed if it is reference. A closed card is not a blank, it is
 * its head, and each head already carries that card's number. Every default is
 * written ONLY when its key is absent, so the member's first click ends this
 * file's involvement in that card forever.
 *
 * FAILS SAFE. Any error, or this file / anime.js missing, and the Locker
 * renders as its original single-column stack with every block intact.
 * To revert: delete this file, its <script> tag in hub-locker.liquid, and the
 * `=== ADDON: locker dashboard layout ===` CSS fence.
 * ========================================================================= */
(function () {
  'use strict';

  /* ---- The declared order. Height plays no part in any of it. ------------
   * Left lane is "act on this" (a checklist, your points, an unclaimed code);
   * right lane is "what you have" (sessions, titles). Anything absent — the
   * member-only cards for a guest, bookings on a deploy where that backend is
   * down — is skipped, and the lanes close up rather than leaving a hole. */
  // #lk-perks leads (owner, 2026-10-13: "move this higher in the locker") - it renders only when a member
  // perk is live, and is skipped like any absent card otherwise.
  var LANE_A = ['#lk-perks', '#lk-onboard', '#lk-membership'];
  var LANE_B = ['#lk-bookings', '#lk-ach', '#lk-challenges'];
  // ≤900px there is one column, so the two lanes interleave back into one
  // priority order rather than playing out left-lane-then-right-lane.
  var STACK = ['#lk-perks', '#lk-onboard', '#lk-membership', '#lk-bookings', '#lk-ach', '#lk-challenges'];

  // Full-width, below the grid, in this order. They want the width: tiers is a
  // 3-across row, saved is an auto-fill card grid.
  var WIDE = ['.lk-tiers-wrap', '#lk-saved', '#lk-saved-empty'];

  // The utility strip that closes the page — signposts and small asks, not
  // status. #lk-rewards-panel joins it ONLY when there is no Membership card to
  // fold it into (i.e. signed out).
  var UTIL = ['.lk-refer', '.lk-suggest'];

  // Cards that get a fold chevron, and the head element that stays visible when
  // they are folded. Every one of these heads is a direct child of its card.
  var FOLDABLE = [
    ['#lk-membership', '.lk-membership__head'],
    ['#lk-bookings', '.lk-bk__head'],
    ['#lk-ach', '.lk-ach__head'],
    ['#lk-challenges', '.lk-chal__head'],
    ['#lk-perks', '.lk-perks__head']
  ];

  var FOLD_KEY = 'hub:lk:fold:';

  /* ---- What is open when you arrive -------------------------------------
   * Eight blocks all rendering expanded is the clutter. These are the chosen
   * FIRST-VISIT states; every one is written only when its key is ABSENT, so
   * from the member's first click onwards their own choice wins and nothing
   * here ever overrides it again.
   *
   * The rule used to pick them: a card stays open if it is something to ACT on
   * or something that changed since last time. It starts closed if it is
   * reference — true, worth having, but not news.
   *
   *   open    Complete your locker  the next action, and it retires itself
   *           Membership            points and tier, the reason members come
   *           Your bookings         time-sensitive, and short
   *           Earn bonus points     only renders when a challenge is LIVE;
   *                                 closing it would hide money on the table
   *           Titles & achievements see below — measured, not reasoned
   *   closed  Your public profile   a mirror of the hero directly above it
   *           Membership tiers      members: their tier is in the card above.
   *                                 Guests: OPEN — for them it is the pitch
   *           Saved                 its head keeps the count chip either way
   *
   * WHY NO DASHBOARD CARD IS FOLDED HERE. Achievements was, and it was measured
   * out again: folding it took that card 542px → 103px but left the right lane
   * 509px shorter than the left, and the page did not get one pixel shorter —
   * the taller lane sets the height, so the saving went straight into a hole.
   * The three that DO start closed are the full-width blocks, where a fold
   * removes real page length: 5,815px → 5,229px on the signed-in preview.
   * A card in a lane is the wrong thing to close by default; that is what the
   * per-card chevrons and Collapse-all are for. Left as a populated map, not
   * deleted, because the next person's instinct will be the same as mine was. */
  var FOLD_DEFAULT = { 'lk-ach': false, 'lk-membership': false, 'lk-bookings': false, 'lk-challenges': false, 'lk-perks': false };

  // The four disclosures that shipped before this file existed. They keep their
  // own keys and their own handlers; only the default is set here.
  var LEGACY = [
    // Your public profile: NO default, i.e. open. It briefly shipped closed and
    // the owner asked for it back — it is where a member's times, trophy case
    // and race results are, and the whole point of connecting Strava is to fill
    // it. Closing the surface the integration feeds is the wrong trade.
    ['lk-preview-toggle', 'hub:preview:collapsed', null],
    ['lk-tiers-toggle', 'hub:tiers:collapsed', function () { return isMember() ? 'true' : 'false'; }],
    ['lk-saved-toggle', 'hub:saved:collapsed', function () { return 'true'; }],
    ['lk-onboard-toggle', 'hub:onboard:collapsed', null]  // no default — it is the next action
  ];

  function $(sel, root) { return (root || document).querySelector(sel); }
  function get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function set(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function isMember() { return document.body.getAttribute('data-member') === 'true'; }

  /* ---- Fold / unfold ---------------------------------------------------- */

  function applyFold(card, folded) {
    card.classList.toggle('is-folded', !!folded);
    var btn = card.querySelector(':scope > .lk-cardtog');
    if (btn) btn.setAttribute('aria-expanded', folded ? 'false' : 'true');
  }

  function makeFoldable(card, head) {
    if (!card || !head || card.querySelector(':scope > .lk-cardtog')) return;
    // .lk-foldable scopes the fold CSS to these cards. A bare `.is-folded` rule
    // in a section stylesheet is a landmine — Shopify concatenates every
    // section's {% stylesheet %} into one file, so it would silently claim that
    // class name for the whole theme.
    card.classList.add('lk-foldable');
    head.setAttribute('data-lk-head', '');

    // The eyebrow first, the heading only as a fallback. Membership's heading is
    // "You're Pro", and "Collapse You're Pro" is not a control label — its
    // eyebrow, "ATHLO+ Membership", is. Achievements has no eyebrow and falls
    // through to its heading, which is already the right words.
    var label = (head.querySelector('.lk-panel__label') || head.querySelector('h2, h3') || {}).textContent || 'this card';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lk-cardtog';
    btn.setAttribute('aria-expanded', 'true');
    btn.setAttribute('aria-label', 'Collapse ' + label.trim());
    // The chevron is CSS (::before) so the button carries no text a screen
    // reader would read out on top of the aria-label.
    card.appendChild(btn);

    var key = FOLD_KEY + card.id;
    if (get(key) === null && FOLD_DEFAULT[card.id]) set(key, '1');
    if (get(key) === '1') applyFold(card, true);

    btn.addEventListener('click', function () {
      var next = !card.classList.contains('is-folded');
      applyFold(card, next);
      set(key, next ? '1' : '0');
      syncTidy();
    });
  }

  /* ---- One control for the whole page -----------------------------------
   * Per-card chevrons let a member tune the locker; they do not answer "this is
   * too much, give me the short version" in one move. This does, and it sits in
   * the hero action row next to Edit profile, which is where the other
   * page-level controls already are.
   *
   * It drives the four pre-existing disclosures by CLICKING them rather than
   * reaching into their state: they own their own keys and handlers in the
   * section, and clicking reuses that logic instead of duplicating it — which
   * is how the two systems would otherwise drift apart. */
  var tidyBtn;

  function legacyBtns() {
    return LEGACY.map(function (l) { return $('#' + l[0]); }).filter(function (b) { return b && b.offsetParent !== null; });
  }

  function anyOpen() {
    var open = false;
    document.querySelectorAll('#locker .lk-foldable').forEach(function (c) {
      if (!c.classList.contains('is-folded') && c.offsetParent !== null) open = true;
    });
    legacyBtns().forEach(function (b) { if (b.getAttribute('aria-expanded') === 'true') open = true; });
    return open;
  }

  function syncTidy() {
    if (!tidyBtn) return;
    var open = anyOpen();
    tidyBtn.querySelector('span').textContent = open ? 'Collapse all' : 'Expand all';
    tidyBtn.setAttribute('aria-label', open ? 'Collapse every card in the locker' : 'Expand every card in the locker');
  }

  function setAll(collapse) {
    document.querySelectorAll('#locker .lk-foldable').forEach(function (c) {
      applyFold(c, collapse);
      set(FOLD_KEY + c.id, collapse ? '1' : '0');
    });
    legacyBtns().forEach(function (b) {
      if ((b.getAttribute('aria-expanded') === 'true') === collapse) b.click();
    });
    syncTidy();
  }

  function addTidy() {
    var row = $('#locker .lk-hero__actions');
    if (!row || $('.lk-tidy', row)) return;
    tidyBtn = document.createElement('button');
    tidyBtn.type = 'button';
    tidyBtn.className = 'lk-action-btn lk-tidy';
    tidyBtn.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<polyline points="4 9 12 3 20 9"/><polyline points="4 15 12 21 20 15"/></svg><span>Collapse all</span>';
    tidyBtn.addEventListener('click', function () { setAll(anyOpen()); });
    row.appendChild(tidyBtn);
    syncTidy();
  }

  /* The section wires the four legacy toggles from HubProfile.evaluate(), which
   * resolves after this script has run — so the label would be computed against
   * buttons that had not read their stored state yet. Re-sync when it lands. */
  function seedLegacy() {
    /* ONE-TIME REPAIR. Between 2026-08-30 and this change the file seeded
     * `hub:preview:collapsed = 'true'`, so every member who opened the locker in
     * that window is carrying it. Dropping the default alone would leave exactly
     * those people — the ones who visited most recently — with the block closed
     * forever, because the seed only ever writes when the key is ABSENT and
     * theirs is not. Flip it once, behind its own flag, so anyone who later
     * chooses to close it keeps that choice. Safe to delete after ~2026-10. */
    if (get('hub:lk:previewfix') !== '1') {
      set('hub:lk:previewfix', '1');
      if (get('hub:preview:collapsed') === 'true') set('hub:preview:collapsed', 'false');
    }
    LEGACY.forEach(function (l) {
      if (!l[2]) return;
      if (get(l[1]) === null) set(l[1], l[2]());
    });
  }

  /* ---- Hide a checklist that is all ticks --------------------------------
   * renderOnboard() already retires the whole card at 100% AND archetype AND
   * Strava. The state this handles is the one in between: profile complete but
   * one of those two still open, where the card retitles itself "Finish strong"
   * and six ✓ chips sit above the two rows that are the actual ask. The chips
   * go; the funnel rows stay. Deliberately NOT the whole card — folding that
   * would hide the only part still worth showing.
   *
   * Stateless on purpose: it reads the percentage every time rather than
   * remembering a decision, so it is right on the pass where the meter is still
   * climbing and right again after. renderOnboard() refills #lk-onboard-items
   * but never touches its `hidden`, so this survives a repaint. */
  function tidyOnboard() {
    var pct = $('#lk-onboard-pct'), items = $('#lk-onboard-items');
    if (!pct || !items) return;
    items.hidden = (pct.textContent || '').trim() === '100%';
  }

  /* ---- Reference material out of the card you look at most ---------------
   * "Ways to earn points" is four chips of rules — up to 575, 1–1.5 per $1,
   * +50 first purchase, +50 referral — sitting under a member's live balance.
   * True, useful once, and read never after that. Its label becomes the
   * disclosure that opens it. */
  function foldEarn() {
    var earn = $('.lk-membership__earn');
    if (!earn || earn.querySelector('.lk-mini-tog')) return;
    var lbl = earn.querySelector('.lk-membership__perks-lbl');
    var list = earn.querySelector('ul');
    if (!lbl || !list) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lk-mini-tog';
    btn.setAttribute('aria-expanded', 'false');
    btn.textContent = lbl.textContent;
    lbl.parentNode.replaceChild(btn, lbl);
    list.hidden = true;
    btn.addEventListener('click', function () {
      list.hidden = !list.hidden;
      btn.setAttribute('aria-expanded', list.hidden ? 'false' : 'true');
    });
  }

  /* ---- One progress bar per number ---------------------------------------
   * The hero renders "Profile 0% complete · 9 to go" with a bar and a chip
   * row; #lk-onboard renders the same percentage, the same bar and the same
   * chips one card below. Whichever you look at, the other is noise. The card
   * wins because it is the one you can act on — but only while it is on
   * screen: renderOnboard() retires the card entirely at 100% + archetype +
   * Strava, and at that point the hero meter is the only one left and has to
   * come back. Re-evaluated on every settle rather than decided once. */
  function dedupeMeter() {
    var meter = $('#lk-complete'), card = $('#lk-onboard');
    if (!meter) return;
    meter.classList.toggle('is-dupe', !!card && !card.hidden);
  }

  /* ---- Strava, where someone will actually see it -------------------------
   * The connect control ships INSIDE #lk-editor. To reach it a member had to
   * press Edit profile and scroll past bio, gender, club, home base, since,
   * birthday, colour, photo, username, title, interests, Build-a-Stat and race
   * results — eleven fields — to find the one integration that fills the locker
   * with data it can get no other way. It now sits in a band directly under the
   * hero for anyone who has not connected, and moves BACK into the editor once
   * they have, because from then on the only control left in it is Unlink and
   * the band would be spending the best space on the page saying "done".
   *
   * THE BLOCK IS MOVED, NEVER RE-AUTHORED. Strava's brand rules are gated by
   * _verify/strava-brand-test.mjs: official artwork only, nothing may write a
   * label over it, 48px height and auto width, and "Powered by Strava" on every
   * surface that shows their data. Moving the real element satisfies every one
   * of those by construction. A second, hand-built button would have to satisfy
   * them all again — and going by the recreated mark this gate exists to block,
   * eventually one of them wouldn't. The copy around it is ours; everything
   * inside #lk-strava-connect is theirs and arrives untouched. */
  function promoteStrava(wrap, hero) {
    var box = $('#lk-strava-connect', wrap);
    if (!box || !hero) return;   // signed out, or the connect UI is switched off
    var editor = $('#lk-editor', wrap);
    var host = null;

    function place() {
      // hub-strava.js is authoritative and writes this once it knows.
      var connected = box.getAttribute('data-connected') === 'true';
      if (connected) {
        if (host) {
          if (editor) editor.appendChild(box);
          if (host.parentNode) host.parentNode.removeChild(host);
          host = null;
        }
        return;
      }
      if (host) return;
      host = document.createElement('section');
      host.id = 'lk-scta';
      host.className = 'lk-scta';
      var txt = document.createElement('div');
      txt.className = 'lk-scta__txt';
      var lbl = document.createElement('p');
      lbl.className = 'lk-panel__label';
      lbl.textContent = 'Verified personal bests';
      var h = document.createElement('h2');
      h.className = 'lk-h2';
      h.textContent = 'Stop typing your times in';
      var p = document.createElement('p');
      p.className = 'lk-note-line';
      // Every clause here is backed. Auto-verification from new activities is
      // what the connect copy already inside the block claims; the link back to
      // the source activity is gated (strava-brand-test §6 — the backend writes
      // activityId on every auto-verified PB and the renderer links it).
      // An earlier draft said a verified PB "is the only kind that counts on the
      // leaderboards". Nothing filters on verified — hub-strava-leaderboard is a
      // CLUB board and does not mention it — so that sentence was invented and
      // is gone. Do not put it back without a filter to point at.
      // 2026-10-01: the block moved in below now carries the consent box, so this copy no
      // longer implies the PBs go anywhere public. They land in the Locker; the box decides
      // whether anyone else sees them.
      p.textContent = 'Connect once. New activities verify your personal bests automatically — no typing times in, and every verified PB links back to the activity on Strava. They stay in your Locker unless you tick the box to show them.';
      txt.appendChild(lbl); txt.appendChild(h); txt.appendChild(p);
      host.appendChild(txt);
      host.appendChild(box);
      hero.parentNode.insertBefore(host, hero.nextSibling);
    }

    place();
    if (window.MutationObserver) {
      new MutationObserver(place).observe(box, { attributes: true, attributeFilter: ['data-connected'] });
    }
  }

  /* ---- One points card --------------------------------------------------
   * The Rewards panel is the only place the redeem UI and the "Open ATHLO
   * Rewards" button live, so it cannot simply be dropped — it is folded into
   * the Membership card, under the progress bar, with the parts that repeat
   * what that card already says hidden by CSS (see .lk-membership .lk-panel). */
  function foldRewardsIn() {
    var mem = $('#lk-membership'), rew = $('#lk-rewards-panel');
    if (!mem || !rew) return false;
    var anchor = mem.querySelector('.lk-membership__perks') || mem.querySelector('.lk-membership__activity');
    var lbl = document.createElement('span');
    lbl.className = 'lk-membership__perks-lbl';
    lbl.textContent = 'Spend your points';
    if (anchor) { mem.insertBefore(lbl, anchor); mem.insertBefore(rew, anchor); }
    else { mem.appendChild(lbl); mem.appendChild(rew); }
    return true;
  }

  /* ---- Build ------------------------------------------------------------ */

  function move(parent, sel, wrap) {
    var el = $(sel, wrap);
    if (!el) return null;
    el.style.margin = '0';
    parent.appendChild(el);
    return el;
  }

  function arrange() {
    var wrap = $('#locker .wrap');
    if (!wrap || $('.lk-dash-layout', wrap)) return;

    // Must run BEFORE the section's renderCollapsibles() reads these keys. It
    // does: this file is `defer`, so it executes while readyState is
    // 'interactive' — ahead of the DOMContentLoaded listener that starts the
    // section's own script.
    seedLegacy();

    // The three panels of .lk-grid become individual cards; the wrapper goes.
    // Order inside it is archetype, rewards, explore — rewards is pulled out by
    // id, so the remaining two keep their relative order whatever changes.
    var gridPanels = [];
    var grid = $('.lk-grid', wrap);
    if (grid) {
      Array.prototype.slice.call(grid.children).forEach(function (p) {
        if (p.classList && p.classList.contains('lk-panel')) gridPanels.push(p);
      });
    }

    var hero = $('#lk-hero', wrap);
    if (!hero) return;

    // 1. Overlays directly under the hero, because that is where the buttons
    //    that open them are — they used to open six blocks further down. Each
    //    is inserted immediately after the hero, so this list lands on the page
    //    in reverse: QR, then avatar builder, then the tall editor last. All
    //    three render hidden, so with none open the hero still touches the card
    //    below it.
    ['#lk-editor', '#lk-avbuild', '#lk-qr'].forEach(function (sel) {
      var el = $(sel, wrap);
      if (el) hero.parentNode.insertBefore(el, hero.nextSibling);
    });

    // 2. Public profile showcase, full width, straight under the overlays.
    var preview = $('#lk-preview', wrap);

    // 3. The two-lane grid.
    var layout = document.createElement('div');
    layout.className = 'lk-dash-layout';
    var colA = document.createElement('div'); colA.className = 'lk-col';
    var colB = document.createElement('div'); colB.className = 'lk-col';
    layout.appendChild(colA); layout.appendChild(colB);
    wrap.appendChild(layout);

    var single = !!(window.matchMedia && window.matchMedia('(max-width: 900px)').matches);
    var cards = [];
    if (single) {
      STACK.forEach(function (s) { var el = move(colA, s, wrap); if (el) cards.push(el); });
    } else {
      LANE_A.forEach(function (s) { var el = move(colA, s, wrap); if (el) cards.push(el); });
      LANE_B.forEach(function (s) { var el = move(colB, s, wrap); if (el) cards.push(el); });
    }

    // 4. Full-width blocks under the grid.
    WIDE.forEach(function (s) { var el = $(s, wrap); if (el) wrap.appendChild(el); });

    // 5. Utility strip.
    var util = document.createElement('div');
    util.className = 'lk-util';
    wrap.appendChild(util);
    var rewardsFolded = foldRewardsIn();
    gridPanels.forEach(function (p) {
      if (p.id === 'lk-rewards-panel' && rewardsFolded) return; // already inside Membership
      p.style.margin = '0';
      util.appendChild(p);
      cards.push(p);
    });
    UTIL.forEach(function (s) { var el = move(util, s, wrap); if (el) cards.push(el); });
    if (grid && grid.parentNode) grid.parentNode.removeChild(grid);
    if (!util.children.length && util.parentNode) util.parentNode.removeChild(util);

    // Keep the showcase between the overlays and the grid.
    if (preview) wrap.insertBefore(preview, layout);

    // 6. Disclosures, then the page-level control that drives all of them.
    FOLDABLE.forEach(function (pair) {
      var card = $(pair[0], wrap);
      if (card) makeFoldable(card, $(pair[1], card));
    });
    addTidy();
    promoteStrava(wrap, hero);
    foldEarn();
    dedupeMeter();

    reveal(cards);

    // The onboarding percentage is written by hub-profile.js after hydrate, and
    // the section wires the four legacy toggles in the same pass — so the
    // Collapse-all label has to be recomputed once that has landed.
    function settle() { tidyOnboard(); dedupeMeter(); foldEarn(); syncTidy(); }
    if (window.HubProfile && window.HubProfile.on) window.HubProfile.on(settle);
    setTimeout(settle, 900);
    setTimeout(settle, 2000);

    // The 1-col ⇄ 2-col switch is the ONE thing that re-orders anything, and it
    // reorders to a declared list, not to a measurement.
    var wasSingle = single;
    window.addEventListener('resize', function () {
      var now = !!(window.matchMedia && window.matchMedia('(max-width: 900px)').matches);
      if (now === wasSingle) return;
      wasSingle = now;
      if (now) STACK.forEach(function (s) { move(colA, s, wrap); });
      else {
        LANE_A.forEach(function (s) { move(colA, s, wrap); });
        LANE_B.forEach(function (s) { move(colB, s, wrap); });
      }
    }, { passive: true });
  }

  function reveal(cards) {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !window.anime || !window.anime.animate || !cards.length) return;
    try {
      window.anime.animate(cards, {
        opacity: [0, 1], y: [18, 0], duration: 600, delay: window.anime.stagger(55), ease: 'outCubic'
      });
    } catch (e) {
      cards.forEach(function (c) { c.style.opacity = ''; c.style.transform = ''; });
    }
  }

  function init() { try { arrange(); } catch (e) { /* fail safe: original stack */ } }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
